<template>
    <pendo-popover-v2
        ref="themePopover"
        class="theme-popover"
        :hover="false"
        placement="right-start"
        container="body"
        keyboard-navigation
        :popper-class="['theme-popover__popover']"
        @show="focusFirstOption">
        <template #default>
            <pendo-button
                class="theme-popover__trigger nav-option"
                pendo-analytics="theme-popover"
                type="tertiary"
                size="small"
                :aria-label="$t('themeMenu.Theme')"
                @mouseenter="showPopover"
                @mouseleave="hidePopover(false)">
                <sun-moon-icon class="theme-popover__option-prefix-icon" />
                <div class="theme-popover__option-label">
                    {{ $t('themeMenu.Theme') }}
                </div>
                <pendo-icon
                    class="theme-popover__option-suffix-icon"
                    type="chevron-right"
                    size="14" />
            </pendo-button>
        </template>
        <template #popover>
            <div
                ref="themePopoverContent"
                class="theme-popover__content"
                @mouseenter="showPopover"
                @mouseleave="hidePopover(false)"
                @keydown.esc="hidePopover(true)">
                <pendo-button
                    v-for="option in options"
                    :key="option.id"
                    class="nav-option theme-popover__option"
                    type="tertiary"
                    :pendo-analytics="option.analytics"
                    :aria-label="option.label"
                    @click.prevent.stop="handleOptionClick(option)"
                    @keydown.space.prevent.stop="handleOptionClick(option)"
                    @keydown.enter.prevent.stop="handleOptionClick(option)"
                    @keydown.tab.prevent.stop="handleOptionClick(option)">
                    <div
                        class="theme-popover__option-tag"
                        :class="{
                            'theme-popover__option-tag--dark': isDark(option.id),
                        }">
                        <p class="theme-popover__option-tag-text">
                            {{ $t('themeMenu.Aa') }}
                        </p>
                    </div>
                    <div class="theme-popover__option-label">
                        {{ option.label }}
                    </div>
                    <pendo-icon
                        v-if="isSelected(option.id)"
                        class="theme-popover__option-suffix-icon"
                        type="check"
                        size="14" />
                </pendo-button>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { PendoButton, PendoIcon, PendoPopoverV2 } from '@pendo/components-vue3';
import SunMoonIcon from '@/top-nav/components/icons/sun-moon-icon.vue';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';

export default {
    name: 'ThemePopover',
    components: {
        PendoButton,
        PendoIcon,
        PendoPopoverV2,
        SunMoonIcon,
    },
    emits: ['select'],
    setup () {
        const { isSystemDarkMode, darkModeInLocalStorage, setDarkMode } = useDarkMode();

        return {
            isSystemDarkMode,
            darkModeInLocalStorage,
            setDarkMode,
        };
    },
    computed: {
        options () {
            return [
                {
                    id: 'system',
                    label: this.$t('themeMenu.System theme'),
                    analytics: 'theme-popover-system-theme',
                    onClick: () => this.setDarkMode('system'),
                },
                {
                    id: 'light',
                    label: this.$t('themeMenu.Light'),
                    analytics: 'theme-popover-light-theme',
                    onClick: () => this.setDarkMode(false),
                },
                {
                    id: 'dark',
                    label: this.$t('themeMenu.Dark'),
                    analytics: 'theme-popover-dark-theme',
                    onClick: () => this.setDarkMode(true),
                },
            ];
        },
    },
    methods: {
        isDark (id) {
            switch (id) {
                case 'system':
                    return this.isSystemDarkMode;
                case 'light':
                    return false;
                case 'dark':
                    return true;
                default:
                    return false;
            }
        },
        isSelected (id) {
            switch (id) {
                case 'system':
                    return this.darkModeInLocalStorage === 'system';
                case 'light':
                    return this.darkModeInLocalStorage === false;
                case 'dark':
                    return this.darkModeInLocalStorage === true;
                default:
                    return false;
            }
        },
        hidePopover (closeParentMenu = false) {
            if (this.$refs.themePopover?.isShown) {
                this.$refs.themePopover?.hide?.();

                if (closeParentMenu) {
                    this.$emit('select');
                }
            }
        },
        showPopover () {
            if (!this.$refs.themePopover?.isShown) {
                this.$refs.themePopover?.show?.();
            }
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.themePopoverContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        handleOptionClick (option) {
            option.onClick();
            this.hidePopover(true);
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.theme-popover {
    padding: 0 !important;
    height: 32px;

    :deep(.trigger) {
        width: 100%;
        height: 100%;
    }

    &__option {
        margin: 0 !important;
        width: 100%;
        padding: 6px 4px !important;
        display: flex !important;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -1px;
        }

        &:hover {
            text-decoration: none;
            background-color: var(--surface-default-hover);
        }

        &-prefix-icon {
            flex-shrink: 0;
            color: var(--text-body-default-base);
        }

        &-suffix-icon {
            margin-left: auto;
            color: var(--text-body-subtle-base);
        }

        :deep(span) {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            height: 32px;

            svg {
                height: 14px;
                width: 14px;
                padding: 5px;
                box-sizing: content-box;
            }
        }

        &-tag {
            font-weight: 400;
            padding: 1px 5px;
            border-radius: 4px;
            border: 1px solid var(--surface-default-border);
            color: var(--static-text-body-default-base);
            background-color: var(--static-text-body-inverse-base);
            display: flex;
            align-items: center;

            &-text {
                margin: 0;
            }

            &--dark {
                color: var(--static-text-body-inverse-base);
                background-color: var(--static-text-body-default-base);
            }

            &::before {
                content: '';
                display: inline-block;
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background-color: var(--palette-teal-70);
                margin-right: 4px;
            }
        }

        &-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__trigger {
        width: 100%;
        transition: none;
        border-radius: 5px;
        padding: 6px 8px !important;
        margin: 0 !important;

        & > :deep(span) {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 4px;
        }

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }

        &:hover {
            text-decoration: none;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 6px;
        width: 200px;
    }
}
</style>

<style lang="less">
.theme-popover__popover {
    z-index: 20001;

    .pendo-popover-v3__panel {
        padding: 0;
    }
}
</style>
